import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch, FaDownload, FaPlus, FaPencilAlt, FaBan, FaCheck, FaChevronLeft, FaChevronRight,
  FaExclamationTriangle, FaCampground, FaReceipt,
} from "react-icons/fa";
import Modal from "../Modal";
import CampamentoModal from "./CampamentoModal";
import CampamentoGastosModal from "./CampamentoGastosModal";

const API = "/api";
const LIMITE = 10;

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Las fechas llegan como YYYY-MM-DD (sin zona horaria): se formatean sin pasar por Date.
const formatoFecha = (fecha) => {
  if (!fecha) return "";
  const [anio, mes, dia] = fecha.split("-");
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${dia} ${meses[Number(mes) - 1]} ${anio}`;
};

const rangoFechas = (c) => (c.fecha_fin && c.fecha_fin !== c.fecha_inicio ? `${formatoFecha(c.fecha_inicio)} – ${formatoFecha(c.fecha_fin)}` : formatoFecha(c.fecha_inicio));

export default function CampamentosAdmin() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("todos");

  const [miembros, setMiembros] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [campamentoAbierto, setCampamentoAbierto] = useState(null);
  const [gastosDe, setGastosDe] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [accionTipo, setAccionTipo] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3500);
  };

  const totalPages = Math.ceil(total / LIMITE);

  useEffect(() => {
    const t = setTimeout(() => { setBusqueda(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const construirQuery = useCallback(
    (pagina, limite) => `page=${pagina}&limit=${limite}&search=${encodeURIComponent(busqueda)}&estatus=${estatusFiltro}`,
    [busqueda, estatusFiltro]
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/campamentos?${construirQuery(page, LIMITE)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo campamentos");
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
      setTotales(result.totales || null);
    } catch (error) {
      console.error(error);
      mostrarToast("Error cargando los campamentos", "error");
    } finally {
      setLoading(false);
    }
  }, [page, construirQuery]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/miembros?estatus=activos`, { credentials: "include" });
        if (!res.ok) throw new Error("Error obteniendo miembros");
        const lista = await res.json();
        setMiembros(lista.map((m) => ({ id_miembro: m.id_miembro, nombre: [m.nombre, m.paterno, m.materno].filter(Boolean).join(" ") })));
      } catch (error) {
        console.error(error);
        mostrarToast("Error cargando la lista de miembros", "error");
      }
    })();
  }, []);

  const abrirNuevo = () => { setCampamentoAbierto(null); setModalAbierto(true); };
  const abrirCampamento = (c) => { setCampamentoAbierto(c.id_campamento); setModalAbierto(true); };

  // =========================
  // EXPORTAR CSV (reporte de campamentos con el filtro actual)
  // =========================
  const exportarCSV = async () => {
    setExportando(true);
    try {
      const filas = [];
      for (let pagina = 1; ; pagina++) {
        const res = await fetch(`${API}/campamentos?${construirQuery(pagina, 100)}`, { credentials: "include" });
        if (!res.ok) throw new Error("Error exportando");
        const result = await res.json();
        filas.push(...(result.data || []));
        if (filas.length >= (result.total || 0) || !(result.data || []).length) break;
      }
      const celda = (valor) => {
        let t = String(valor ?? "");
        if (/^[=+\-@\t\r]/.test(t)) t = `'${t}`;
        return `"${t.replace(/"/g, '""')}"`;
      };
      const lineas = [["Campamento", "Tema", "Fecha", "Asistentes", "Beca otorgada", "Monto recaudado", "Monto pendiente", "Debería recaudarse", "Gastos", "Estatus"].map(celda).join(",")];
      filas.forEach((c) => {
        lineas.push([c.nombre, c.tema || "", rangoFechas(c), c.asistentes, c.becas, c.recaudado, c.pendiente, c.deberia, c.gastos, c.activo ? "Activo" : "Inactivo"].map(celda).join(","));
      });
      const blob = new Blob(["﻿" + lineas.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "campamentos.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      mostrarToast("No se pudo exportar", "error");
    } finally {
      setExportando(false);
    }
  };

  const abrirConfirm = (c, tipo) => { setSeleccionado(c); setAccionTipo(tipo); setConfirmOpen(true); };

  const ejecutarAccion = async () => {
    try {
      const desactivar = accionTipo === "desactivar";
      const res = await fetch(
        desactivar ? `${API}/campamentos/${seleccionado.id_campamento}` : `${API}/campamentos/${seleccionado.id_campamento}/reactivar`,
        { method: desactivar ? "DELETE" : "PATCH", credentials: "include" }
      );
      if (!res.ok) throw new Error("Error cambiando estatus");
      setConfirmOpen(false);
      mostrarToast(desactivar ? "Campamento desactivado" : "Campamento reactivado");
      cargar();
    } catch (error) {
      console.error(error);
      mostrarToast("No se pudo cambiar el estatus", "error");
    }
  };

  const inicioRango = total === 0 ? 0 : (page - 1) * LIMITE + 1;
  const finRango = Math.min(page * LIMITE, total);
  const botonAccion = "w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 transition";
  const th = "px-4 py-3 font-semibold";

  return (
    <div className="p-6">

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] max-w-sm px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600">Administración</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">Campamentos</span>
      </div>

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Campamentos</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Reporte de cada campamento: becas, lo recaudado, lo pendiente y lo que debería recaudarse. Abre uno para ver sus asistentes y registrar abonos.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarCSV}
            disabled={exportando}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaDownload className="text-xs" /> {exportando ? "Exportando..." : "Exportar"}
          </button>
          <button
            onClick={abrirNuevo}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <FaPlus className="text-xs" /> Nuevo campamento
          </button>
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por campamento, tema, asistente o iglesia..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => { setEstatusFiltro(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* REPORTE */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className={`${th} text-left`}>Campamento</th>
                <th className={`${th} text-right`}>Asistentes</th>
                <th className={`${th} text-right`}>$ Beca otorgada</th>
                <th className={`${th} text-right`}>$ Monto recaudado</th>
                <th className={`${th} text-right`}>$ Monto pendiente</th>
                <th className={`${th} text-right`} title="Cuotas de todos los asistentes menos las becas">$ Debería recaudarse</th>
                <th className={`${th} text-right`} title="Suma de los gastos registrados del campamento">$ Gastos</th>
                <th className={`${th} text-left`}>Estatus</th>
                <th className={`${th} text-right`}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">Cargando...</td></tr>
              ) : data.length > 0 ? (
                data.map((c) => (
                  <tr key={c.id_campamento} className="border-t border-slate-100 hover:bg-slate-50 transition">
                    <td className="px-4 py-3 min-w-[260px]">
                      <button onClick={() => abrirCampamento(c)} className="flex items-center gap-2 text-left">
                        <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                          <FaCampground className="text-xs" />
                        </span>
                        <span>
                          <span className="block font-medium text-slate-800 hover:text-indigo-600">{c.nombre}</span>
                          <span className="block text-xs text-slate-400">{rangoFechas(c)}{c.tema ? ` · ${c.tema}` : ""}</span>
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{c.asistentes}</td>
                    <td className="px-4 py-3 text-right text-sky-600 tabular-nums">{formatoMoneda(c.becas)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600 font-medium tabular-nums">{formatoMoneda(c.recaudado)}</td>
                    <td className={`px-4 py-3 text-right font-medium tabular-nums ${c.pendiente > 0 ? "text-amber-600" : "text-slate-400"}`}>{formatoMoneda(c.pendiente)}</td>
                    <td className="px-4 py-3 text-right text-slate-800 tabular-nums">{formatoMoneda(c.deberia)}</td>
                    <td className={`px-4 py-3 text-right font-medium tabular-nums ${c.gastos > 0 ? "text-rose-600" : "text-slate-400"}`}>{formatoMoneda(c.gastos)}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className={`w-2 h-2 rounded-full ${c.activo ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {c.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => abrirCampamento(c)} title="Ver / editar" className={`${botonAccion} hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200`}>
                          <FaPencilAlt className="text-xs" />
                        </button>
                        <button onClick={() => setGastosDe(c)} title="Gastos" aria-label={`Gastos de ${c.nombre}`} className={`${botonAccion} hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200`}>
                          <FaReceipt className="text-xs" />
                        </button>
                        {c.activo ? (
                          <button onClick={() => abrirConfirm(c, "desactivar")} title="Desactivar" className={`${botonAccion} hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200`}>
                            <FaBan className="text-xs" />
                          </button>
                        ) : (
                          <button onClick={() => abrirConfirm(c, "reactivar")} title="Reactivar" className={`${botonAccion} hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200`}>
                            <FaCheck className="text-xs" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">Sin resultados</td></tr>
              )}
            </tbody>

            {!loading && data.length > 0 && totales && (
              <tfoot className="bg-slate-50 border-t-2 border-slate-200 text-sm font-semibold">
                <tr>
                  <td className="px-4 py-3 text-slate-600" colSpan={2}>Total ({total} {total === 1 ? "campamento" : "campamentos"})</td>
                  <td className="px-4 py-3 text-right text-sky-600 tabular-nums">{formatoMoneda(totales.becas)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600 tabular-nums">{formatoMoneda(totales.recaudado)}</td>
                  <td className="px-4 py-3 text-right text-amber-600 tabular-nums">{formatoMoneda(totales.pendiente)}</td>
                  <td className="px-4 py-3 text-right text-slate-800 tabular-nums">{formatoMoneda(totales.deberia)}</td>
                  <td className="px-4 py-3 text-right text-rose-600 tabular-nums">{formatoMoneda(totales.gastos)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-2">Debería recaudarse = cuotas de todos los asistentes − becas. Recaudado + pendiente = debería recaudarse. Gastos = suma de los gastos registrados (botón <FaReceipt className="inline text-[10px] align-baseline" /> en Acciones).</p>

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {total === 0 ? "Sin resultados" : `Mostrando ${inicioRango} a ${finRango} de ${total} resultados`}
        </span>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(page - 1)} className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition">
            <FaChevronLeft className="text-xs" />
          </button>
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">{page}</span>
          <button disabled={page === totalPages || totalPages === 0} onClick={() => setPage(page + 1)} className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition">
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>

      {/* DETALLE / ALTA */}
      <CampamentoModal
        open={modalAbierto}
        idCampamento={campamentoAbierto}
        miembros={miembros}
        onClose={() => { setModalAbierto(false); cargar(); }}
        onChanged={() => cargar()}
      />

      {/* GASTOS DEL CAMPAMENTO */}
      <CampamentoGastosModal
        open={!!gastosDe}
        campamento={gastosDe}
        onClose={() => setGastosDe(null)}
        onChanged={() => cargar()}
      />

      {/* CONFIRMAR DESACTIVAR / REACTIVAR */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${accionTipo === "desactivar" ? "bg-rose-50 text-rose-500" : "bg-emerald-50 text-emerald-500"}`}>
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">{accionTipo === "desactivar" ? "Desactivar" : "Reactivar"} campamento</h3>
          <p className="text-sm text-slate-500 mb-6">
            {accionTipo === "desactivar"
              ? `"${seleccionado?.nombre}" ya no se podrá modificar ni recibir abonos, pero sus datos se conservan.`
              : `"${seleccionado?.nombre}" volverá a poder modificarse y recibir abonos.`}
          </p>
          <div className="flex gap-2">
            <button onClick={() => setConfirmOpen(false)} className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
            <button
              onClick={ejecutarAccion}
              className={`w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition ${accionTipo === "desactivar" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
